import { type DocumentData, type DocumentSnapshot, doc, onSnapshot } from 'firebase/firestore'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { db } from '@/services/firebase/config'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'
import { useUserData } from '@/store/userData/useUserData'

// The rolling invite-allowance counter mirrored off the profile doc (spec D6), with the
// Firestore Timestamp already reduced to epoch ms so consumers stay Timestamp-free.
export interface InviteQuotaState {
  periodStartMs: number
  used: number
}

interface ProfileContextValue {
  // null while the /users/{uid} check is in flight (or signed out); once known, `false`
  // means an inert account — authenticated but never claimed an invite (no profile doc).
  hasProfile: boolean | null
  // Granted-privilege strings on the profile doc (spec D7) — e.g. the invite tiers. Empty
  // when signed out, no profile, or none granted. Read-only here; the client can't write it.
  capabilities: string[]
  // The invite-allowance counter, or null when no invite has been sent this window.
  inviteQuota: InviteQuotaState | null
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined)

// Reads the invite fields off a snapshot's data defensively — the client never writes them,
// but a malformed doc must not throw. Returns [] / null on anything unexpected.
function readCapabilities(data: Record<string, unknown> | undefined): string[] {
  const raw = data?.capabilities
  return Array.isArray(raw) ? raw.filter((c): c is string => typeof c === 'string') : []
}

function readInviteQuota(data: Record<string, unknown> | undefined): InviteQuotaState | null {
  const raw = data?.inviteQuota as { periodStart?: { toMillis?: () => number }; used?: unknown } | undefined
  if (raw && typeof raw.used === 'number' && typeof raw.periodStart?.toMillis === 'function') {
    return { periodStartMs: raw.periodStart.toMillis(), used: raw.used }
  }
  return null
}

// The FE half of the profile-doc gate (enforced server-side): tracks whether the
// signed-in user has a /users/{uid} doc, which the owner-read rule lets them read. ProtectedRoute
// routes accounts without one to the No-Invite screen instead of a data-denied /home.
// It also surfaces the invite `capabilities` + `inviteQuota` off the same snapshot, so the
// "Invite a friend" launcher and allowance render reactively without a second listener.
export function ProfileProvider({ children }: { children: ReactNode }) {
  const { uid } = useUserData()
  const [hasProfile, setHasProfile] = useState<boolean | null>(null)
  const [capabilities, setCapabilities] = useState<string[]>([])
  const [inviteQuota, setInviteQuota] = useState<InviteQuotaState | null>(null)

  useEffect(() => {
    if (!uid) {
      setHasProfile(null)
      setCapabilities([])
      setInviteQuota(null)
      return
    }
    setHasProfile(null) // re-checking on a new uid — ProtectedRoute shows a loading state meanwhile
  }, [uid])

  // Recovery is delegated to useLiveSubscription and the shared connection ladder — this listener used to
  // own a flat 3s retry, so a dead transport got hammered on two uncoordinated clocks. On error the hook
  // leaves state untouched, so a transient failure can't strand a member on /no-invite.
  useLiveSubscription<DocumentSnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'profile',
    subscribe: (activeUid, { onData, onError }) => onSnapshot(doc(db, 'users', activeUid), onData, onError),
    onData: (snapshot) => {
      setHasProfile(snapshot.exists())
      const data = snapshot.data()
      setCapabilities(readCapabilities(data))
      setInviteQuota(readInviteQuota(data))
    }
  })

  return <ProfileContext.Provider value={{ hasProfile, capabilities, inviteQuota }}>{children}</ProfileContext.Provider>
}

export function useProfile(): ProfileContextValue {
  const context = useContext(ProfileContext)
  if (!context) {
    throw new Error('useProfile must be used within ProfileProvider')
  }
  return context
}
